import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { Heading, LoadingSpinner, PageContainer, Section } from '../../../shared/ui';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import { useYowanAuthGuard } from '../app/yowanAuthGuards';
import {
  fetchYowanRelatedSeminarList,
} from '../app/yowanContentClient';
import {
  AdminListPagination,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
const { yowanPageContent } = require('./pageContent');
const { getYowanRouteMeta } = require('../routes/manifest');

export const STAKEHOLDER_PAGE_SIZE = 20;

export const getStakeholderSeminarHref = (seminarId, search = '') =>
  appendYowanPreviewQueryIfNeeded(`/stakeholder/seminars/${seminarId}`, search);

export const getStakeholderClassesHref = (search = '') =>
  appendYowanPreviewQueryIfNeeded('/stakeholder/classes', search);

export const getStakeholderClassHref = (classId, search = '') =>
  appendYowanPreviewQueryIfNeeded(`/stakeholder/classes/${classId}`, search);

export const getStakeholderClassApplicationsHref = (classId, search = '') =>
  appendYowanPreviewQueryIfNeeded(`/stakeholder/classes/${classId}/applications`, search);

export const getStakeholderApplicationsHref = (seminarId, search = '') =>
  appendYowanPreviewQueryIfNeeded(`/stakeholder/seminars/${seminarId}/applications`, search);

export const getStakeholderMessagesHref = (seminarId, search = '') =>
  appendYowanPreviewQueryIfNeeded(`/stakeholder/seminars/${seminarId}/application-messages`, search);

const hasMeaningfulStakeholderAccess = (access = null, profileKind = '') => {
  if (!access) {
    return false;
  }

  if (Array.isArray(access)) {
    return access.some((item) => hasMeaningfulStakeholderAccess(item, profileKind));
  }

  if (typeof access !== 'object') {
    return false;
  }

  if (access.isActive === false || access.is_active === false) {
    return false;
  }

  const profileIdKeys = profileKind === 'organizer'
    ? ['organizerId', 'organizer_id']
    : ['instructorId', 'instructor_id'];

  return [
    'id',
    'uuid',
    'accessId',
    'access_id',
    'profileId',
    'profile_id',
    'authSubjectId',
    'auth_subject_id',
    'loginEmail',
    'login_email',
    'authSubjectEmail',
    'auth_subject_email',
    ...profileIdKeys,
  ].some((key) => Boolean(access[key])) ||
    hasMeaningfulStakeholderAccess(access.access, profileKind) ||
    hasMeaningfulStakeholderAccess(access.item, profileKind) ||
    hasMeaningfulStakeholderAccess(access.profile, profileKind) ||
    hasMeaningfulStakeholderAccess(access.authSubject || access.auth_subject, profileKind) ||
    hasMeaningfulStakeholderAccess(access.results, profileKind) ||
    hasMeaningfulStakeholderAccess(access.items, profileKind);
};

export const getStakeholderRoleState = (currentUser = {}) => {
  const hasInstructorProfile = hasMeaningfulStakeholderAccess(currentUser.instructorAccess, 'instructor');
  const hasOrganizerProfile = hasMeaningfulStakeholderAccess(currentUser.organizerAccess, 'organizer');
  const hasInstructorOnly = hasInstructorProfile && !hasOrganizerProfile;
  const hasOrganizerOnly = hasOrganizerProfile && !hasInstructorProfile;
  const subjectPrefix = hasOrganizerOnly ? '主催' : hasInstructorOnly ? '担当' : '関係';

  return {
    hasInstructorProfile,
    hasOrganizerProfile,
    hasInstructorOnly,
    hasOrganizerOnly,
    subjectPrefix,
    classesLabel: `${subjectPrefix}教室・講座`,
    seminarsLabel: `${subjectPrefix}セミナー`,
    classesLead: `${subjectPrefix}している教室・講座と申込状況を確認できます。`,
    seminarsLead: `${subjectPrefix}しているセミナーと申込状況を確認できます。`,
  };
};

export const buildStakeholderNavItems = (content = {}, currentUser = {}) => {
  const roleState = getStakeholderRoleState(currentUser);

  return (content.navItems || [])
    .filter((item) => {
      if (item.href === '/stakeholder/instructor-profile') {
        return roleState.hasInstructorProfile;
      }

      if (item.href === '/stakeholder/organizer-profile') {
        return roleState.hasOrganizerProfile;
      }

      return true;
    })
    .map((item) => {
      if (item.href === '/stakeholder/classes') {
        return { ...item, label: roleState.classesLabel };
      }

      if (item.href === '/stakeholder/seminars') {
        return { ...item, label: roleState.seminarsLabel };
      }

      return item;
    });
};

export const buildStakeholderHeaderContent = (content = {}, currentUser = {}) => {
  const roleState = getStakeholderRoleState(currentUser);

  if (content.navKind === 'classes') {
    return {
      title: roleState.classesLabel,
      paragraphs: [
        roleState.classesLead,
        'この画面は閲覧専用です。編集や申込キャンセルは管理者が行います。',
      ],
      searchPlaceholder: content.searchPlaceholder?.replace('教室・講座', roleState.classesLabel) || content.searchPlaceholder,
    };
  }

  if (content.navKind === 'seminars') {
    return {
      title: roleState.seminarsLabel,
      paragraphs: [
        roleState.seminarsLead,
        'この画面は閲覧専用です。編集、中止、申込キャンセル、参加者へのメール送信は管理者が行います。',
      ],
      searchPlaceholder: content.searchPlaceholder?.replace('セミナー', roleState.seminarsLabel) || content.searchPlaceholder,
    };
  }

  return {
    title: content.header?.title,
    paragraphs: content.header?.paragraphs || [],
    searchPlaceholder: content.searchPlaceholder,
  };
};

export const useStakeholderPageMetadata = (routeKey) => {
  const metadata = getYowanRouteMeta(routeKey);

  useEffect(() => {
    if (!metadata) {
      return;
    }

    document.title = metadata.title;

    if (metadata.description) {
      const descriptionTag = document.querySelector('meta[name="description"]');
      if (descriptionTag) {
        descriptionTag.setAttribute('content', metadata.description);
      }
    }
  }, [metadata]);
};

export const StakeholderGuardNotice = ({ notice }) => {
  useStakeholderPageMetadata('/stakeholder');

  if (!notice) {
    return null;
  }

  return (
    <Section className="yowan-admin-page yowan-stakeholder-page">
      <PageContainer>
        <div className="yowan-admin-form c-surface-content">
          <p className="yowan-admin-form__message">{notice.message}</p>
          {notice.actionHref ? (
            <p className="yowan-admin-form__actions">
              <Link to={notice.actionHref}>{notice.actionLabel || '移動する'}</Link>
            </p>
          ) : null}
        </div>
      </PageContainer>
    </Section>
  );
};

export const useStakeholderGuard = (guardOptions = {}) => {
  const location = useLocation();
  const guard = useYowanAuthGuard({
    requiresAuth: true,
    search: location.search,
    ...guardOptions,
    requiresRelatedSeminars: false,
  });

  if (guard.status !== 'ready') {
    return guard;
  }

  const currentUser = guard.currentUser || {};
  const roleState = getStakeholderRoleState(currentUser);
  const canAccessStakeholder = Boolean(
    currentUser.canAccessRelatedSeminars ||
    currentUser.canAccessRelatedClasses ||
    currentUser.isInstructor ||
    currentUser.isOrganizer ||
    roleState.hasInstructorProfile ||
    roleState.hasOrganizerProfile,
  );

  if (!canAccessStakeholder) {
    return {
      ...guard,
      status: 'stakeholder-required',
      notice: {
        message:
          'このページは講師・主催者として関係している教室・講座やセミナーを確認する関係者向けのページです。閲覧権限を確認できませんでした。',
        actionHref: appendYowanPreviewQueryIfNeeded('/mypage', location.search),
        actionLabel: 'マイページへ',
      },
    };
  }

  return guard;
};

const useStakeholderShellState = () => {
  const shellRef = useRef(null);
  const resizingRef = useRef(false);
  const [compactSidebarMode, setCompactSidebarMode] = useState(false);
  const [sidebarHidden, setSidebarHidden] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(232);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const syncSidebarState = (event) => {
      setCompactSidebarMode(event.matches);
      setSidebarHidden(event.matches);
    };

    syncSidebarState(mediaQuery);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', syncSidebarState);
      return () => mediaQuery.removeEventListener('change', syncSidebarState);
    }

    mediaQuery.addListener(syncSidebarState);
    return () => mediaQuery.removeListener(syncSidebarState);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handlePointerMove = (event) => {
      if (!resizingRef.current || !shellRef.current) {
        return;
      }

      const bounds = shellRef.current.getBoundingClientRect();
      const rawWidth = event.clientX - bounds.left;

      if (rawWidth <= 132) {
        setSidebarHidden(true);
        return;
      }

      const nextWidth = Math.min(Math.max(rawWidth, 168), 420);
      setSidebarWidth(nextWidth);
      setSidebarHidden(false);
    };

    const handlePointerUp = () => {
      resizingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, []);

  return {
    compactSidebarMode,
    resizingRef,
    shellRef,
    sidebarHidden,
    sidebarWidth,
    setSidebarHidden,
    setSidebarWidth,
  };
};

export const StakeholderShell = ({
  activeNavHref = '/stakeholder',
  routeKey = '/stakeholder',
  currentUser = {},
  children,
}) => {
  const preservedDomainSearch = usePreservedDomainSearch();
  const content = yowanPageContent['/stakeholder'];
  useStakeholderPageMetadata(routeKey);
  const navItems = buildStakeholderNavItems(content, currentUser);
  const {
    compactSidebarMode,
    resizingRef,
    shellRef,
    sidebarHidden,
    sidebarWidth,
    setSidebarHidden,
    setSidebarWidth,
  } = useStakeholderShellState();

  return (
    <Section className="yowan-admin-page yowan-stakeholder-page">
      <PageContainer>
        <div
          ref={shellRef}
          className={`yowan-admin-shell yowan-stakeholder-shell${sidebarHidden ? ' yowan-admin-shell--sidebar-hidden yowan-stakeholder-shell--sidebar-hidden' : ''}`}
          style={{
            '--admin-sidebar-width': `${sidebarWidth}px`,
            '--stakeholder-sidebar-width': `${sidebarWidth}px`,
          }}
        >
          <aside className="yowan-admin-shell__sidebar yowan-stakeholder-shell__sidebar">
            <div className="yowan-admin-sidebar yowan-stakeholder-sidebar">
              {!sidebarHidden ? (
                <button
                  type="button"
                  className="yowan-admin-shell__toggle yowan-admin-shell__toggle--close"
                  aria-label="左ナビを隠す"
                  aria-expanded="true"
                  onClick={() => setSidebarHidden(true)}
                >
                  <span aria-hidden="true">‹</span>
                </button>
              ) : null}
              <Heading as="h1" align="left">
                <Link
                  className="yowan-admin-sidebar__home-link"
                  to={appendYowanPreviewQueryIfNeeded('/stakeholder', preservedDomainSearch)}
                  aria-current={activeNavHref === '/stakeholder' ? 'page' : undefined}
                >
                  {content.header.title}
                </Link>
              </Heading>
              <section className="yowan-admin-sidebar__group">
                <h2 className="yowan-admin-sidebar__group-title">関係者</h2>
                <ul className="yowan-admin-sidebar__list">
                  {navItems.map((item) => (
                    <li key={item.href}>
                      <Link
                        to={appendYowanPreviewQueryIfNeeded(item.href, preservedDomainSearch)}
                        aria-current={item.href === activeNavHref ? 'page' : undefined}
                      >
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            </div>
          </aside>

          {sidebarHidden ? (
            <button
              type="button"
              className="yowan-admin-shell__toggle yowan-admin-shell__toggle--open"
              aria-label="左ナビを表示"
              aria-expanded="false"
              onClick={() => {
                setSidebarWidth(Math.max(sidebarWidth, 208));
                setSidebarHidden(false);
              }}
            >
              <span aria-hidden="true">›</span>
            </button>
          ) : null}

          {!sidebarHidden ? (
            <button
              type="button"
              className="yowan-admin-shell__resizer"
              aria-label="左ナビの幅を調整"
              onPointerDown={(event) => {
                event.preventDefault();
                resizingRef.current = true;
                document.body.style.cursor = 'col-resize';
                document.body.style.userSelect = 'none';
              }}
            />
          ) : null}

          <main className="yowan-admin-shell__main yowan-stakeholder-shell__main">
            <div
              className="yowan-admin-shell__content yowan-admin-stack"
              onClick={() => {
                if (compactSidebarMode && !sidebarHidden) {
                  setSidebarHidden(true);
                }
              }}
            >
              {children}
            </div>
          </main>
        </div>
      </PageContainer>
    </Section>
  );
};

export const StakeholderSearchToolbar = ({
  searchValue,
  onSearchChange,
  searchPlaceholder,
  metaText,
}) => (
  <div className="yowan-admin-toolbar c-surface-content">
    <div className="yowan-admin-toolbar__group">
      <label className="c-form__field yowan-admin-toolbar__field">
        <span className="c-form__label">検索</span>
        <input
          className="c-form__control yowan-admin-toolbar__search"
          type="search"
          value={searchValue}
          placeholder={searchPlaceholder}
          onChange={onSearchChange}
        />
      </label>
    </div>
    {metaText ? <p className="yowan-admin-toolbar__meta">{metaText}</p> : null}
  </div>
);

export const getSeminarSearchText = (seminar = {}) => [
  seminar.title,
  seminar.subtitle,
  seminar.organizerName,
  seminar.standardInstructor,
  seminar.nextSlotLabel,
  seminar.area,
].filter(Boolean).join(' ').toLowerCase();

const StakeholderSeminarsPage = ({ guardOptions }) => {
  const content = yowanPageContent['/stakeholder/seminars'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const guard = useStakeholderGuard(guardOptions);
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [pageInfo, setPageInfo] = useState({
    count: 0,
    pageSize: STAKEHOLDER_PAGE_SIZE,
    next: null,
    previous: null,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const headerContent = buildStakeholderHeaderContent(content, guard.currentUser);

  useEffect(() => {
    if (guard.status !== 'ready') {
      return undefined;
    }

    let isMounted = true;
    setIsLoading(true);
    setLoadError('');

    fetchYowanRelatedSeminarList({
      page,
      pageSize: STAKEHOLDER_PAGE_SIZE,
    })
      .then((payload) => {
        if (!isMounted) {
          return;
        }

        setRecords(payload.results || []);
        setPageInfo({
          count: payload.count || 0,
          pageSize: payload.pageSize || STAKEHOLDER_PAGE_SIZE,
          next: payload.next,
          previous: payload.previous,
        });
      })
      .catch(() => {
        if (!isMounted) {
          return;
        }

        setRecords([]);
        setPageInfo({
          count: 0,
          pageSize: STAKEHOLDER_PAGE_SIZE,
          next: null,
          previous: null,
        });
        setLoadError(`${headerContent.title}を読み込めませんでした。`);
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [guard.status, headerContent.title, page]);

  const visibleRecords = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    if (!normalizedQuery) {
      return records;
    }

    return records.filter((record) => getSeminarSearchText(record).includes(normalizedQuery));
  }, [records, searchQuery]);

  if (guard.status !== 'ready') {
    return <StakeholderGuardNotice notice={guard.notice} />;
  }

  return (
    <StakeholderShell activeNavHref="/stakeholder/seminars" routeKey="/stakeholder/seminars" currentUser={guard.currentUser}>
      <AdminPageHeader
        title={headerContent.title}
        paragraphs={headerContent.paragraphs}
      />

      <StakeholderSearchToolbar
        searchPlaceholder={headerContent.searchPlaceholder}
        searchValue={searchQuery}
        onSearchChange={(event) => setSearchQuery(event.target.value)}
        metaText={`${visibleRecords.length}件表示`}
      />

      <div className="yowan-admin-resource-table-wrap c-surface-content">
        {isLoading ? <LoadingSpinner label={`${headerContent.title}を読み込み中`} showLabel /> : null}
        {!isLoading && loadError ? <p className="yowan-admin-form__message">{loadError}</p> : null}
        {!isLoading && !loadError && visibleRecords.length === 0 ? (
          <p className="yowan-admin-form__message">表示できる{headerContent.title}はありません。</p>
        ) : null}
        {!isLoading && !loadError && visibleRecords.length > 0 ? (
          <table className="yowan-admin-resource-table yowan-stakeholder-table yowan-stakeholder-table--seminars">
            <thead>
              <tr>
                <th>セミナー</th>
                <th>開催日時</th>
                <th>講師</th>
                <th>申込</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {visibleRecords.map((record) => (
                <tr key={record.id}>
                  <td>
                    <p className="yowan-admin-resource-table__title">{record.title || '-'}</p>
                    {record.subtitle ? <p className="yowan-admin-resource-table__subline">{record.subtitle}</p> : null}
                    {record.organizerName ? <p className="yowan-admin-resource-table__subline">主催: {record.organizerName}</p> : null}
                  </td>
                  <td>{record.nextSlotLabel || record.datetime || '-'}</td>
                  <td>{record.standardInstructor || '-'}</td>
                  <td>{record.applicationsCount || 0}名</td>
                  <td>
                    <div className="yowan-admin-resource-table__actions">
                      <Link to={getStakeholderSeminarHref(record.id, preservedDomainSearch)}>詳細</Link>
                      <Link to={getStakeholderApplicationsHref(record.id, preservedDomainSearch)}>申込</Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </div>

      <AdminListPagination
        currentPage={page}
        totalPages={Math.max(1, Math.ceil((pageInfo.count || visibleRecords.length) / (pageInfo.pageSize || STAKEHOLDER_PAGE_SIZE)))}
        onPageChange={setPage}
      />
    </StakeholderShell>
  );
};

export default StakeholderSeminarsPage;
